const CLIENT_ERROR_SOURCE = 'client_feedback';
const API_ERROR_SOURCE = 'api';

function normalizedMessage(error, fallback = 'Неизвестная ошибка') {
  const message = String(error?.message || '').trim();
  return message || fallback;
}

function normalizedStatus(error) {
  const status = Number(error?.status || 0);
  return Number.isInteger(status) && status >= 0 ? status : 0;
}

function normalizedCode(error, fallback = 'unknown_error') {
  const code = String(error?.code || '').trim();
  return code || fallback;
}

export class UiFeedbackError extends Error {
  constructor(message, { code = 'ui_feedback', details = null, category = 'input' } = {}) {
    super(String(message || 'Некорректные данные.'));
    this.name = 'UiFeedbackError';
    this.source = CLIENT_ERROR_SOURCE;
    this.code = String(code || 'ui_feedback');
    this.status = 0;
    this.details = details;
    this.category = String(category || 'input');
  }
}

export function isAbortError(error) {
  return error?.name === 'AbortError' || error?.code === 'request_aborted';
}

export function classifyClientError(error) {
  if (isAbortError(error)) {
    return {
      silent: true,
      eventType: '',
      level: 'debug',
      noticeType: 'notice',
      message: '',
      action: '',
      payload: {},
    };
  }

  const status = normalizedStatus(error);
  const code = normalizedCode(error);
  const message = normalizedMessage(error);
  const action = String(error?.action || '').trim();
  const source = String(error?.source || '').trim();
  const details = error?.details ?? null;

  if (source === CLIENT_ERROR_SOURCE || error?.name === 'UiFeedbackError') {
    return {
      silent: false,
      eventType: 'ui.feedback.rejected',
      level: 'warning',
      noticeType: 'warning',
      message,
      action,
      payload: { source: CLIENT_ERROR_SOURCE, category: String(error?.category || 'input'), code, status, details },
    };
  }

  const isApiError = source === API_ERROR_SOURCE || error?.name === 'ApiError' || status > 0;
  if (isApiError) {
    if (action.startsWith('sync.') && (code === 'invalid_response' || code === 'network_error' || code === 'request_failed' || status >= 500)) {
      return {
        silent: true,
        eventType: 'sync.background.error',
        level: 'warning',
        noticeType: 'warning',
        message,
        action,
        payload: { source: API_ERROR_SOURCE, category: 'background_sync', code, status, details },
      };
    }
    if (code === 'invalid_response') {
      return {
        silent: false,
        eventType: 'api.invalid_response',
        level: 'error',
        noticeType: 'error',
        message,
        action,
        payload: { source: API_ERROR_SOURCE, category: 'invalid_response', code, status, details },
      };
    }
    if (status === 400 || status === 422) {
      return {
        silent: false,
        eventType: 'ui.validation.rejected',
        level: 'warning',
        noticeType: 'warning',
        message,
        action,
        payload: { source: API_ERROR_SOURCE, category: 'validation', code, status, details },
      };
    }
    if (status === 401) {
      return {
        silent: false,
        eventType: 'ui.authentication.required',
        level: 'warning',
        noticeType: 'warning',
        message,
        action,
        payload: { source: API_ERROR_SOURCE, category: 'authentication', code, status, details },
      };
    }
    if (status === 403) {
      return {
        silent: false,
        eventType: 'ui.access.denied',
        level: 'warning',
        noticeType: 'warning',
        message,
        action,
        payload: { source: API_ERROR_SOURCE, category: 'authorization', code, status, details },
      };
    }
    if (status === 404) {
      return {
        silent: false,
        eventType: 'ui.resource.not_found',
        level: 'warning',
        noticeType: 'warning',
        message,
        action,
        payload: { source: API_ERROR_SOURCE, category: 'not_found', code, status, details },
      };
    }
    if (status === 409) {
      const lockOwner = code === 'record_locked' ? String(details?.owner_login || '').trim() : '';
      return {
        silent: false,
        eventType: 'ui.conflict.rejected',
        level: 'warning',
        noticeType: 'warning',
        message: lockOwner ? `${message} Редактирует: ${lockOwner}.` : message,
        action,
        payload: { source: API_ERROR_SOURCE, category: 'conflict', code, status, details },
      };
    }
    if (status === 429) {
      return {
        silent: false,
        eventType: 'ui.rate_limited',
        level: 'warning',
        noticeType: 'warning',
        message,
        action,
        payload: { source: API_ERROR_SOURCE, category: 'rate_limit', code, status, details },
      };
    }
    if (status >= 400 && status < 500) {
      return {
        silent: false,
        eventType: 'ui.request.rejected',
        level: 'warning',
        noticeType: 'warning',
        message,
        action,
        payload: { source: API_ERROR_SOURCE, category: 'request', code, status, details },
      };
    }
    if (code === 'network_error') {
      return {
        silent: false,
        eventType: 'ui.network.unavailable',
        level: 'warning',
        noticeType: 'warning',
        message,
        action,
        payload: { source: API_ERROR_SOURCE, category: 'network', code, status, details },
      };
    }
    return {
      silent: false,
      eventType: 'ui.service.error',
      level: 'error',
      noticeType: 'error',
      message,
      action,
      payload: { source: API_ERROR_SOURCE, category: 'service', code, status, details },
    };
  }

  return {
    silent: false,
    eventType: 'ui.runtime.error',
    level: 'error',
    noticeType: 'error',
    message,
    action,
    payload: {
      source: 'runtime',
      category: 'unexpected',
      code,
      status,
      name: String(error?.name || 'Error'),
    },
  };
}
